import { useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useUserPreferences,
  useCreateKeywordWatch,
  useDeleteKeywordWatch,
} from '@/hooks/queries'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Settings — Keyword watches.
 *
 * Wave-1 P5 settings primitive UI. Lets the user add and remove keyword
 * watches. Watches are surfaced via `useUserPreferences` (envelope hook
 * already in place) and consumed by `usePersonalisedScore`.
 *
 * Add/remove flows go through the dedicated CRUD endpoints
 * (`POST /preferences/:userId/keywords`, `DELETE …/keywords/:id`) rather
 * than the merge `PUT /preferences/:userId` so the user sees row-level
 * persistence + IDs immediately.
 */
export function KeywordWatchesSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const createKeyword = useCreateKeywordWatch()
  const deleteKeyword = useDeleteKeywordWatch()

  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  const watches = preferences?.keywordWatches ?? []

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    const trimmed = draft.trim()
    if (!trimmed) {
      setError('Keyword cannot be empty.')
      return
    }
    if (trimmed.length > 500) {
      setError('Keyword is too long (max 500 characters).')
      return
    }
    if (watches.some((w) => w.keyword.toLowerCase() === trimmed.toLowerCase())) {
      setError(`"${trimmed}" is already in your watch list.`)
      return
    }
    createKeyword.mutate(
      { userId, keyword: trimmed },
      {
        onSuccess: () => setDraft(''),
        onError: () => setError('Failed to add keyword. Please try again.'),
      },
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Keyword watches</p>
        <p className="text-xs text-muted-foreground">
          Boost ranking of stories containing these keywords.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <Input
          aria-label="Add keyword"
          placeholder="e.g. ransomware, supply-chain, lockbit"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={500}
        />
        <Button
          type="submit"
          size="sm"
          disabled={createKeyword.isPending || !userId}
        >
          {createKeyword.isPending ? 'Adding…' : 'Add'}
        </Button>
      </form>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      {watches.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No keyword watches yet.
        </p>
      ) : (
        <div
          className="flex flex-wrap gap-2"
          role="list"
          aria-label="Keyword watches"
        >
          {watches.map((w) => (
            <span
              key={w.id}
              role="listitem"
              className="inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-1 text-xs"
            >
              {w.keyword}
              <button
                type="button"
                onClick={() => deleteKeyword.mutate({ userId, id: w.id })}
                // R28: the user's own keyword watch, in an attribute.
                data-ph-no-capture
                aria-label={`Remove keyword ${w.keyword}`}
                className="-mr-0.5 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
